import { useAuth, useDashboard } from 'providers/Auth';

import { mutations } from 'entities';
import { appConfig } from 'config/app';

import { Button, Column } from 'components/Blocks';
import { LogoBubble } from 'components/Logos';
import { CircleXIcon, LogoutIcon, WarningIcon } from 'components/Blocks/Icons';

import { UserEmail } from './UserEmail';
import { ConfirmButton } from './ConfirmButton';
import { Coin } from 'components/DecorativeIcons';

import { useSurveys } from 'components/SurveyList/surveys';
import { useLang } from 'locale';
import { useWindowSize } from 'hooks';

import { getFirst } from 'utils/array';

export function AccountOptions() {
  const { t } = useLang();

  const [
    {
      user: { firstName, age, gender, cityText, stateText, hasEmailNotifications },
    },
    { updateUser },
  ] = useDashboard();

  const { logout, deleteSession } = useAuth();

  const subscribe = mutations.users.subscribeToEmails.use({
    onSuccess: () => updateUser({ hasEmailNotifications: true }),
  });

  const unsubscribe = mutations.users.unsubscribeFromEmails.use({
    onSuccess: () => updateUser({ hasEmailNotifications: false }),
  });

  const closeAccount = mutations.users.closeAccount.use({
    onSuccess: deleteSession,
  });

  const { availableSurveys } = useSurveys();

  const [shownSurvey] = availableSurveys;

  const { breakpoints } = useWindowSize();

  return (
    <Column className="items-center pt-10 small_tablet:mb-2 small_tablet:pt-6" gap="8">
      <Column className="small_tablet:[&_svg]:size-20" alignItems="center">
        <LogoBubble />

        <Column className="items-center" gap="2">
          <strong className="text-center text-xl font-semibold text-foreground-title">
            {firstName}
          </strong>

          <Column className="text-foreground-secondary text-center text-sm" gap="1">
            <UserEmail />

            <span>
              {age} · {t(`gender.${gender}`)}
            </span>

            <span>
              {cityText}, {stateText}
            </span>
          </Column>
        </Column>
      </Column>

      <Column>
        {!!shownSurvey && breakpoints.SMALL_TABLET && (
          <Button
            color="primary"
            className="border-warning bg-background text-warning hover:text-primary"
            variant="outlined"
            round
            size="large"
            href={shownSurvey.url}
            linkTarget={shownSurvey.id}
          >
            <Coin size={24} />
            {getFirst(availableSurveys).incentives} Encuesta Especial
          </Button>
        )}

        {appConfig.requestDataUrl && (
          <Button
            color="primary"
            className="border-border bg-background text-foreground hover:text-primary"
            variant="outlined"
            round
            size="large"
            href={appConfig.requestDataUrl}
            linkTarget={'request-data'}
          >
            {t('request-my-data')}
          </Button>
        )}

        <ConfirmButton
          action={hasEmailNotifications ? unsubscribe.execute : subscribe.execute}
          title={
            hasEmailNotifications
              ? t('disable-email-notifications')
              : t('enable-email-notifications')
          }
          description={
            hasEmailNotifications
              ? t('disable-email-notification-description')
              : t('enable-email-notification-description')
          }
          icon={<WarningIcon color="warning" size={40} />}
          text={
            hasEmailNotifications
              ? t('disable-email-notifications')
              : t('enable-email-notifications')
          }
        />

        <ConfirmButton
          title={t('delete-account-title')}
          description={t('delete-account-description')}
          icon={<CircleXIcon color="destructive" size={40} variant="filled" />}
          text={t('delete-account')}
          action={closeAccount.execute}
        />

        <ConfirmButton
          title={t('logout-title')}
          description={t('logout-description')}
          icon={<LogoutIcon color="primary" size={40} />}
          text={t('logout')}
          confirmText={t('logout')}
          action={logout}
        />
      </Column>
    </Column>
  );
}
